<script>
	import { url } from '@roxi/routify';
	import { getData, postFormData, postJsonData } from "../helpers.js";
	import { activeTabs, entitledTabs, declinedIntegrations, toggleDeclined, subscriptionType, featuresLoaded, loadFeatures, refreshFeatures } from "../stores/integrations.js";
	import { lifetimeValid, accountValid, accountValidityError, userInfoLoaded, loadUserInfo } from '../stores/userInfo.js';
	import Navbar from "../components/Navbar.svelte";
	import Footer from "../components/Footer.svelte";

	loadFeatures();
	loadUserInfo();

	// Same Stripe checkout-session redirect as user-info-menu.svelte's
	// "Update Billing Information" button.
	function paymentRedirect() {
		getData('stripe/get-checkout-session', {'hostURL': window.location.href})
		.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				window.location.href = ret_json.url;
			}
		});
	}

	// Core subscription group: activated/deactivated together via Stripe billing.
	const coreCatalog = [
		{ id: 'ecpr',       name: 'DIR eCPR',        monogram: 'CPR',  color: '#1d4ed8',
		  description: 'Certified payroll records from the CA DIR eCPR portal. Searching is free with your login; the Core subscription adds bulk download with CSV export.',
		  path: './cpr-search' },
		{ id: 'pwc100',     name: 'DIR PWC100',      monogram: 'PWC',  color: '#4338ca',
		  description: 'Public works project registrations (PWC-100). Searching is free with your login; the Core subscription adds the CSV report download.',
		  path: './pwc100-search' },
	];

	// flag: null = freemium (always active). comingSoon entries ignore flag/path.
	// pitch entries are not built yet: amber pill + Contact Us (no flag/path).
	// funded: customer-funded build in progress; flips to Active on deployment.
	// partner: relationship established with agreed re-engage trigger (first
	// funding customer); Contact Us surfaces that customer. Lex: 2023 correspondence.
	// free/enterprise cards use the local visibility toggle (no billing call).
	// Order: the enforcement pipeline, start to finish. (a) the Score ranks
	// companies; (b) payroll is pulled and the wage/hour discrepancy $ calculated
	// (Court Package engine, GPS ground truth, private payroll ingestion), with
	// the Report as the packaged baseline; (c)(d) worker education/feedback
	// (WorkersRights); (e) feedback rank-orders investigations (Flagged Leads);
	// (f) Lex prices outcomes and selects cases for trade pension fund customers;
	// Live Monitoring watches the selected companies; expert testimony carries
	// the case home. Customers who know the space will read the sequence.
	const catalog = [
		{ id: 'wage-theft-score', name: 'Wage Theft Score', monogram: 'WTS', color: '#9ca3af',
		  description: 'Like a credit score, but for wage compliance: a ranked score of a company\'s propensity to pay workers fairly, built from payroll, enforcement, and project data. Piloted internally with strong results.', comingSoon: true, interest: true },
		{ id: 'court-package', name: 'Wage & Hour Court Package', monogram: 'WHC', color: '#be123c',
		  description: 'Court-ready wage and hour violation submittal with full time calculations: shift start and end, travel time, shop start time, and unpaid-hours damages. Calculations run against PADG-held payroll and enforcement data; priced per case.', contact: true },
		{ id: 'gps-timecard', name: 'GPS Timecard Reconstruction', monogram: 'GPS', color: '#15803d',
		  description: 'Rebuild start, end, and travel time from GPS waypoint data: commercial fleet telematics and ELD records (discoverable from the employer), GPS timeclock apps, or the worker\'s own phone location history. Proven in litigation; priced per case.', contact: true },
		{ id: 'private-pdf', name: 'db Private', monogram: 'PDF', color: '#b45309',
		  description: 'Upload and search your own payroll PDFs.',
		  flag: 'PRIVATE_SEARCH', path: './db-core',       comingSoon: false, enterprise: true },
		{ id: 'wage-theft-report', name: 'Wage Theft Report', monogram: 'WTR', color: '#7c3aed',
		  description: 'A company report, like a credit report for labor compliance: payroll, enforcement, and project history in one document — $250 per report. The Wage Theft Score will sit at the top once it launches.', contact: true },
		{ id: 'workers-rights', name: 'WorkersRights App', monogram: 'WR',  color: '#0f766e',
		  description: 'Worker-facing app that educates workers on their rights and collects complaints, feeding the sensory.PR data inflow.', downNote: true, free: true },
		{ id: 'lexocad',     name: 'Flagged Leads',       monogram: 'FL',   color: '#0e7490',
		  description: 'For enforcement agencies: we run your closed violation cases through our labor-plausibility engine and show it finding them on its own — then point it at your open project list. Ranked leads, with an evidence packet behind every flag. Funded pilot engagement.', contact: true },
		{ id: 'lex-machina', name: 'Lex Machina',         monogram: 'LEX',  color: '#b91c1c',
		  description: 'Track and analyze construction industry labor standards violations inside Lex Machina Legal Analytics: wage and hour disputes, misclassification, and workplace safety cases. Partner relationship in place since 2023; the first customer engagement funds the integration build.', partner: true,
		  link: 'https://developer.lexmachina.com/getting-started/overview/', linkText: 'the Lex Machina developer portal' },
		{ id: 'live-monitoring', name: 'Live Company Monitoring', monogram: 'MON', color: '#0369a1',
		  description: 'Live monitoring of companies by name or demographic across the eCPR, PWC-100, and DLSE stack, plus agency meeting agendas. In build under a customer-funded engagement.', funded: true, interest: true },
		{ id: 'expert-witness', name: 'Expert Witness Testimony', monogram: 'SME', color: '#1e3a8a',
		  description: 'PADG findings carried into deposition and trial: expert witness testimony by Dr. Forest Peterson — Ph.D. in Civil Engineering from Stanford, a decade of union field experience, and the data systems behind every number presented.', contact: true,
		  link: 'https://scholar.google.com/citations?user=x9sykTEAAAAJ', linkText: 'Dr. Peterson\'s publications on Google Scholar' },
		{ id: 'dlse',       name: 'DLSE Enforcement', monogram: 'DLSE', color: '#047857',
		  description: 'Labor Commissioner enforcement and violation search.',
		  flag: null,             path: './wage-report',   comingSoon: false, free: true },

		/* Hidden pending Jasmine's cut (Jul 6) — restore by moving above. Rationale:
		   DLSE temporarily down; APM blocked on procurement; CustomDataServices
		   umbrella covered by Court Package / GPS / WTR; rest never shopped.
		{ id: 'cslb',        name: 'CSLB',                monogram: 'CSLB', color: '#9ca3af',
		  description: 'Contractor license integration from the CSLB to populate the system with contractors from outside the PWCR.', comingSoon: true },
		{ id: 'dodge',       name: 'Dodge Construction Network', monogram: 'DCN', color: '#9ca3af',
		  description: 'Construction project data from the Dodge Construction Network to populate the Projects list with non-PWC projects.', pitch: true,
		  link: 'https://www.construction.com/apis/', linkText: 'construction.com APIs' },
		{ id: 'custom-data', name: 'CustomDataServices',  monogram: 'CDS',  color: '#7e22ce',
		  description: 'Custom data pipelines built to your spec.', contact: true },
		{ id: 'private-db-cp', name: 'Private db (Companies, Projects)', monogram: 'DB', color: '#9ca3af',
		  description: 'Private database of your companies and projects.', comingSoon: true },
		{ id: 'additional-states', name: 'Additional States', monogram: 'US', color: '#9ca3af',
		  description: 'Certified payroll and public works data from states beyond California.', comingSoon: true },
		{ id: 'agency-portfolio', name: 'Agency Portfolio Monitoring', monogram: 'APM', color: '#0e7490',
		  description: 'For awarding agencies: compliance monitoring across your project portfolio — verify contractors are clear of violations and track your list of active projects in the system.', interest: true },
		*/
	];

	// Entitlement comes from backend/Stripe via the shared store (paywall-features
	// + subscription-type); catalog ids match INTEGRATION_TABS ids.
	// Pass $activeTabs for entitled AND not locally declined; $entitledTabs for
	// backend entitlement only.
	function inTabs(item, tabs) {
		if (item.comingSoon) return false;
		return tabs.some(t => t.id === item.id);
	}

	// "Register Interest" cards (interest: true): one click recorded per
	// account on the backend (user/integration-interest), no email involved.
	let interestedIds = [];
	let interestBusy = {};   // card id -> request in flight
	let interestError = {};  // card id -> request failed

	getData('user/integration-interest')
		.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success' && Array.isArray(ret_json.interested)) {
				interestedIds = ret_json.interested;
			}
		})
		.catch(() => {});

	function registerInterest(id) {
		interestBusy[id] = true;
		interestError[id] = false;
		postJsonData('user/integration-interest', { integration_id: id })
		.then(response => response.json())
		.then(ret_json => {
			interestBusy[id] = false;
			if (ret_json.status === 'success') {
				interestedIds = [...interestedIds, id];
			} else {
				interestError[id] = true;
			}
		})
		.catch(() => {
			interestBusy[id] = false;
			interestError[id] = true;
		});
	}

	// mailto silently fails on machines with no mail handler, so clicking a
	// contact button also reveals the address with a copy fallback.
	let revealed = {};   // card id -> address box shown
	let copiedId = null; // card id whose address was just copied

	function copyEmail(id, email) {
		navigator.clipboard.writeText(email);
		copiedId = id;
		setTimeout(() => { if (copiedId === id) copiedId = null; }, 2000);
	}

	// Core subscription billing: same endpoint/fields as update-subscription.svelte
	// (POST stripe/change-subscription with sub_type + password verification).
	$: onMonthly = $subscriptionType === 'MONTHLY';

	let coreForm;
	let coreBusy = false;
	let corePasswordError = '';
	let coreSubError = '';
	let coreSuccessText = '';

	function handleCoreSubscription() {
		coreBusy = true;
		corePasswordError = '';
		coreSubError = '';
		coreSuccessText = '';
		const wasMonthly = onMonthly;
		postFormData('stripe/change-subscription', coreForm)
		.then(response => response.json())
		.then(ret_json => {
			coreBusy = false;
			if (ret_json.status === 'success') {
				coreForm.reset();
				coreSuccessText = wasMonthly
					? 'Core subscription cancelled. Tabs will update.'
					: 'Core subscription activated. Tabs will update.';
				// Re-fetch subscription-type + paywall-features so the navbar,
				// dashboard card, and badges react without a page reload.
				refreshFeatures();
			} else if (ret_json.status === 'error') {
				let errors = ret_json.error;
				corePasswordError = errors.password ? errors.password[0] : '';
				coreSubError = errors.sub_type ? errors.sub_type[0] : (errors.subscription_type ? errors.subscription_type[0] : '');
			}
		})
		.catch(() => {
			coreBusy = false;
			coreSubError = 'An unknown error occurred. Please try again.';
		});
	}
</script>

<main class="background">
	<Navbar/>
	<div class="lg:mx-48 md:mx-32 sm:mx-8 mx-2">
		<div class="mt-10 mb-4">
			<div class="px-8 py-6 lg:px-16 lg:py-8 card">
				<p class="text-2xl font-semibold text-blue-900 mb-4">Account</p>
				<div class="text-xl font-normal mb-6">
					{#if $userInfoLoaded}
					<p>Account Status: {$accountValid ? 'Valid' : 'Invalid'}</p>
					{#if !$accountValid}
					<p class="text-red-500">{$accountValidityError}</p>
					{/if}
					{/if}
					<p class="{$subscriptionType ? '' : 'hidden'}">Subscription Type: {$subscriptionType}</p>
					{#if $lifetimeValid}
					<p>Account type: Enterprise</p>
					{:else}
					<p>Account type: Standard</p>
					{/if}
				</div>
				<button on:click={paymentRedirect}
				class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
				Update Billing Information</button>
			</div>
		</div>
		<div class="px-8 py-6 lg:px-16 lg:py-12 card">
			<p class="text-2xl font-semibold text-blue-900 mb-2">Integrations</p>
			<p class="text-lg text-gray-600 mb-8">Data sources and tools available on your dashboard.</p>

			<!-- Core Subscription group -->
			<div class="border-2 border-blue-500 rounded-xl p-6 mb-8 bg-blue-50">
				<div class="flex flex-wrap items-center justify-between gap-2 mb-4">
					<p class="text-xl font-semibold text-blue-900">Core Downloader Subscription &mdash; $79.95 per month</p>
					{#if $featuresLoaded}
						<span class="px-3 py-1 rounded-full text-sm {onMonthly ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}">
							{onMonthly ? 'Subscribed' : 'Not Subscribed'}
						</span>
					{/if}
				</div>
				<p class="text-gray-600 mb-4">The Core subscription covers downloading results as CSV. Searching and previewing records stays free with your login.</p>
				<div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
					{#each coreCatalog as item}
						<div class="border rounded-lg p-6 shadow-sm flex flex-col bg-white">
							<div class="flex items-center mb-4">
								<!-- inline SVG tile -->
								<svg width="48" height="48" viewBox="0 0 48 48" class="mr-4 shrink-0">
									<rect width="48" height="48" rx="10" fill={item.color}/>
									<text x="24" y="24" text-anchor="middle" dominant-baseline="central"
										fill="white" font-family="monospace" font-weight="bold"
										font-size={item.monogram.length > 3 ? 11 : 14}>
										{item.monogram}
									</text>
								</svg>
								<p class="text-lg font-semibold">{item.name}</p>
							</div>
							<p class="text-gray-600 mb-4 grow">{item.description}</p>
							{#if !$featuresLoaded}
								<span class="self-start px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-500">...</span>
							{:else if inTabs(item, $activeTabs)}
								<span class="self-start px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Active</span>
							{:else}
								<span class="self-start px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700">Included in Core</span>
							{/if}
							{#if item.id === 'ecpr'}
								<!-- Advanced eCPR features are an enterprise engagement via PADG -->
								<div class="mt-4 flex items-center justify-between">
									<span class="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700">Advanced</span>
									<a href="mailto:information@paloaltodatagroup.com" on:click={() => revealed[item.id] = true}>
										<button class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm">Activate Enterprise Features</button>
									</a>
								</div>
								{#if revealed[item.id]}
									<div class="mt-2 flex items-center gap-2 text-sm bg-gray-100 rounded-md px-2 py-1">
										<span class="select-all grow">information@paloaltodatagroup.com</span>
										<button type="button" on:click={() => copyEmail(item.id, 'information@paloaltodatagroup.com')}
											class="px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-200 cursor-pointer">
											{copiedId === item.id ? 'Copied' : 'Copy'}
										</button>
									</div>
								{/if}
							{/if}
						</div>
					{/each}
				</div>

				<!-- Activate/Deactivate via Stripe (same flow as update-subscription) -->
				{#if $featuresLoaded}
					<form bind:this={coreForm} on:submit|preventDefault={handleCoreSubscription}
						class="flex flex-wrap items-center gap-4">
						<input type="hidden" name="sub_type" value={onMonthly ? 'NONE' : 'MONTHLY'}>
						<input name="password" type="password" required placeholder="Verify Password"
							class="rounded-md shadow-sm py-1.5 px-3 border {corePasswordError === '' ? 'border-gray-300' : 'border-red-600'}"/>
						<button type="submit" disabled={coreBusy}
							class="px-6 py-1.5 rounded-md {onMonthly ? 'bg-gray-500 hover:bg-gray-700' : 'bg-blue-500 hover:bg-blue-700'} text-gray-100 font-semibold border-none shadow-sm disabled:opacity-50">
							{coreBusy ? 'Processing...' : (onMonthly ? 'Deactivate' : 'Activate')}
						</button>
						{#if corePasswordError !== ''}
							<p class="text-red-600">{corePasswordError}</p>
						{/if}
						{#if coreSubError !== ''}
							<p class="text-red-600">{coreSubError}</p>
						{/if}
						{#if coreSuccessText !== ''}
							<p class="text-green-700">{coreSuccessText}</p>
						{/if}
					</form>
					{#if onMonthly}
						<p class="text-sm text-gray-500 mt-2">Deactivating cancels at the end of the current billing period.</p>
					{/if}
				{/if}
			</div>

			<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
				{#each catalog as item}
					<div class="border rounded-lg p-6 shadow-sm flex flex-col
						{item.comingSoon ? 'opacity-60 bg-gray-50' : 'bg-white'}">
						<div class="flex items-center mb-4">
							<!-- inline SVG tile -->
							<svg width="48" height="48" viewBox="0 0 48 48" class="mr-4 shrink-0">
								<rect width="48" height="48" rx="10" fill={item.color}/>
								<text x="24" y="24" text-anchor="middle" dominant-baseline="central"
									fill="white" font-family="monospace" font-weight="bold"
									font-size={item.monogram.length > 3 ? 11 : 14}>
									{item.monogram}
								</text>
							</svg>
							<p class="text-lg font-semibold">{item.name}</p>
						</div>
						<p class="text-gray-600 mb-4 {item.id === 'dlse' || item.id === 'workers-rights' || item.link ? '' : 'grow'}">{item.description}</p>
						{#if item.link}
							<p class="text-sm text-gray-500 mb-4 grow">
								Learn more at
								<a class="underline text-blue-600 visited:text-blue-600" href={item.link} target="_blank" rel="noopener">{item.linkText}</a>.
							</p>
						{/if}
						{#if item.id === 'dlse'}
							<p class="text-sm text-gray-500 mb-4 grow">
								Available free thanks to donated time from the
								<a class="underline text-blue-600 visited:text-blue-600" href="https://github.com/wagetheft" target="_blank" rel="noopener">DataKind Hackathon</a>
								in support of the
								<a class="underline text-blue-600 visited:text-blue-600" href="https://wagetheftcoalition.org/" target="_blank" rel="noopener">Wage Theft Coalition</a>.
							</p>
						{/if}
						{#if item.id === 'workers-rights'}
							<p class="text-sm text-gray-500 mb-4 grow">
								Developed by the
								<a class="underline text-blue-600 visited:text-blue-600" href="https://calawyers.org/labor-and-employment-law/labor-employment-rights-workers-rights-app/" target="_blank" rel="noopener">California Lawyers Association</a>
								Labor and Employment Law Section in collaboration with the
								<a class="underline text-blue-600 visited:text-blue-600" href="https://wagetheftcoalition.org/" target="_blank" rel="noopener">Wage Theft Coalition</a>.
							</p>
						{/if}

						{#if item.comingSoon}
							<div class="flex flex-wrap items-center gap-2">
								<span class="px-3 py-1 rounded-full text-sm bg-gray-200 text-gray-600">Coming Soon</span>
								{#if item.interest}
									{#if interestedIds.includes(item.id)}
										<span class="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Interest registered</span>
									{:else}
										<button on:click={() => registerInterest(item.id)} disabled={interestBusy[item.id]}
											class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm disabled:opacity-50 cursor-pointer">
											{interestBusy[item.id] ? 'Registering...' : 'Register Interest'}
										</button>
									{/if}
								{/if}
							</div>
						{:else if item.funded}
							<!-- Customer-funded build: flips to Active on deployment -->
							<div class="flex flex-wrap items-center gap-2">
								<span class="px-3 py-1 rounded-full text-sm bg-sky-100 text-sky-700">In Build &mdash; Customer Funded</span>
								<button disabled title="Available on deployment"
									class="px-4 py-1.5 rounded-md bg-gray-500 text-gray-100 font-semibold border-none shadow-sm opacity-50 cursor-not-allowed">
									Activate</button>
								{#if item.interest}
									{#if interestedIds.includes(item.id)}
										<span class="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Interest registered</span>
									{:else}
										<button on:click={() => registerInterest(item.id)} disabled={interestBusy[item.id]}
											class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm disabled:opacity-50 cursor-pointer">
											{interestBusy[item.id] ? 'Registering...' : 'Register Interest'}
										</button>
									{/if}
								{/if}
							</div>
						{:else if item.partner}
							<!-- Standing agreement (2023): build proceeds when a customer funds it; card's job is surfacing that customer -->
							<div class="flex flex-col items-start gap-2">
								<span class="px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-700">Partnership in Place</span>
								<a href="mailto:support@paloaltodatagroup.com" on:click={() => revealed[item.id] = true}>
									<button class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm">Contact Us</button>
								</a>
							</div>
							<p class="text-sm text-gray-500 mt-2">The build proceeds with the first customer engagement &mdash; contact us to be that customer.</p>
							{#if revealed[item.id]}
								<div class="mt-2 flex items-center gap-2 text-sm bg-gray-100 rounded-md px-2 py-1">
									<span class="select-all grow">support@paloaltodatagroup.com</span>
									<button type="button" on:click={() => copyEmail(item.id, 'support@paloaltodatagroup.com')}
										class="px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-200 cursor-pointer">
										{copiedId === item.id ? 'Copied' : 'Copy'}
									</button>
								</div>
							{/if}
						{:else if item.downNote}
							<div class="flex items-center gap-2 self-start">
								{#if item.free}
									<span class="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Free</span>
								{/if}
								<span class="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700">Temporarily down &mdash; back soon</span>
							</div>
						{:else if item.pitch}
							<!-- Not built yet: pitch for the engagement via email, same reveal/copy pattern as contact -->
							<div class="flex flex-col items-start gap-2">
								<span class="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700">Open offer to collaborate</span>
								<a href="mailto:support@paloaltodatagroup.com" on:click={() => revealed[item.id] = true}>
									<button class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm">Contact Us</button>
								</a>
							</div>
							<p class="text-sm text-gray-500 mt-2">We have a standing offer to work with them &mdash; your interest helps make it happen.</p>
							{#if revealed[item.id]}
								<div class="mt-2 flex items-center gap-2 text-sm bg-gray-100 rounded-md px-2 py-1">
									<span class="select-all grow">support@paloaltodatagroup.com</span>
									<button type="button" on:click={() => copyEmail(item.id, 'support@paloaltodatagroup.com')}
										class="px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-200 cursor-pointer">
										{copiedId === item.id ? 'Copied' : 'Copy'}
									</button>
								</div>
							{/if}
						{:else if item.interest}
							<!-- Register Interest: recorded server-side per account, no email -->
							<div class="flex items-center justify-between">
								<span class="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700">Pilot Program</span>
								{#if interestedIds.includes(item.id)}
									<span class="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Interest registered</span>
								{:else}
									<button on:click={() => registerInterest(item.id)} disabled={interestBusy[item.id]}
										class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm disabled:opacity-50 cursor-pointer">
										{interestBusy[item.id] ? 'Registering...' : 'Register Interest'}
									</button>
								{/if}
							</div>
							{#if interestedIds.includes(item.id)}
								<p class="text-sm text-gray-500 mt-2">Thanks — we'll reach out as this program develops.</p>
							{:else if interestError[item.id]}
								<p class="text-sm text-red-600 mt-2">Could not register your interest. Please try again.</p>
							{:else}
								<p class="text-sm text-gray-500 mt-2">One click registers your interest with your account &mdash; no email needed.</p>
							{/if}
						{:else if item.contact}
							<!-- Custom engagement: no billing flow; scoped per matter via email -->
							<div class="flex items-center justify-between">
								<span class="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700">Custom Engagement</span>
								<a href="mailto:support@paloaltodatagroup.com" on:click={() => revealed[item.id] = true}>
									<button class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm">Contact Us</button>
								</a>
							</div>
							{#if revealed[item.id]}
								<div class="mt-2 flex items-center gap-2 text-sm bg-gray-100 rounded-md px-2 py-1">
									<span class="select-all grow">support@paloaltodatagroup.com</span>
									<button type="button" on:click={() => copyEmail(item.id, 'support@paloaltodatagroup.com')}
										class="px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-200 cursor-pointer">
										{copiedId === item.id ? 'Copied' : 'Copy'}
									</button>
								</div>
							{/if}
						{:else if !$featuresLoaded}
							<span class="self-start px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-500">...</span>
						{:else if item.free}
							<!-- Free: no billing; Activate/Deactivate toggles local tab visibility -->
							<div class="flex items-center justify-between">
								<span class="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Free</span>
								{#if $declinedIntegrations.includes(item.id)}
									<button on:click={() => toggleDeclined(item.id)}
										class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm">Activate</button>
								{:else}
									<button on:click={() => toggleDeclined(item.id)}
										class="px-4 py-1.5 rounded-md bg-gray-500 hover:bg-gray-700 text-gray-100 font-semibold border-none shadow-sm">Deactivate</button>
								{/if}
							</div>
						{:else if inTabs(item, $entitledTabs)}
							{#if item.enterprise}
								<!-- Enterprise, entitled: no local toggle; activation and deactivation
								     go through PADG (backend config), so this is always Active. -->
								<span class="self-start px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Active</span>
								<p class="text-sm text-gray-500 mt-2">To deactivate an enterprise subscription, contact
									<a class="underline text-blue-600 visited:text-blue-600" href="mailto:information@paloaltodatagroup.com">information@paloaltodatagroup.com</a>.</p>
							{:else}
								<div class="flex items-center justify-between">
									{#if $declinedIntegrations.includes(item.id)}
										<span class="px-3 py-1 rounded-full text-sm bg-gray-200 text-gray-600">Inactive</span>
										<button on:click={() => toggleDeclined(item.id)}
											class="px-4 py-1.5 rounded-md bg-blue-500 hover:bg-blue-700 text-gray-100 font-semibold border-none shadow-sm">Activate</button>
									{:else}
										<span class="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">Active</span>
										<button on:click={() => toggleDeclined(item.id)}
											class="px-4 py-1.5 rounded-md bg-gray-500 hover:bg-gray-700 text-gray-100 font-semibold border-none shadow-sm">Deactivate</button>
									{/if}
								</div>
							{/if}
						{:else}
							<!-- Enterprise, not entitled: same action as update-subscription's Enterprise button -->
							<div class="flex items-center justify-between">
								<span class="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700">Enterprise</span>
								<a href="mailto:support@paloaltodatagroup.com" on:click={() => revealed[item.id] = true}>
									<button class="px-4 py-1.5 rounded-md bg-gray-500 hover:bg-gray-700 text-gray-100 font-semibold border-none shadow-sm">Enterprise (Contact Us)</button>
								</a>
							</div>
							{#if revealed[item.id]}
								<div class="mt-2 flex items-center gap-2 text-sm bg-gray-100 rounded-md px-2 py-1">
									<span class="select-all grow">support@paloaltodatagroup.com</span>
									<button type="button" on:click={() => copyEmail(item.id, 'support@paloaltodatagroup.com')}
										class="px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-200 cursor-pointer">
										{copiedId === item.id ? 'Copied' : 'Copy'}
									</button>
								</div>
							{/if}
						{/if}
					</div>
				{/each}
			</div>
		</div>
		<Footer/>
	</div>
</main>

<style>
	main.background {
		background-color: theme("colors.gray.100");
	}
</style>
